import { Collapsible } from "@base-ui/react/collapsible";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";

import { useAccommodationFilterContext } from "../filters/accommodation-filter-context";

import type { AccommodationCount, FilteredArea } from "./_utils/area-filtering";
import { sortSites } from "./_utils/area-sorting";
import { AreaHeader } from "./area-header";
import { SiteSection } from "./site-section";

type AreaSectionProps = Omit<ComponentProps<typeof SiteSection>, "site"> & {
  area: FilteredArea;
  accommodationCount: AccommodationCount | undefined;
  hidden: boolean;
  dimmed: boolean;
};

/**
 * エリアフィルタ変更時に Collapsible の開閉をリセットするフック。
 *
 * 本来は key によるリマウントが自然だが、AreaSection は描画コストが高く
 * hidden で表示切替する設計のため、リマウントを避けて state を直接更新している。
 * @see https://react.dev/reference/react/useState#storing-information-from-previous-renders
 */
function useAreaSectionOpen(areaSlug: string, areaFilter: ReadonlySet<string>) {
  const shouldBeOpen = areaFilter.size === 0 || areaFilter.has(areaSlug);
  const [open, setOpen] = useState(shouldBeOpen);
  const [prevAreaFilter, setPrevAreaFilter] = useState(areaFilter);
  if (prevAreaFilter !== areaFilter) {
    setPrevAreaFilter(areaFilter);
    setOpen(shouldBeOpen);
  }
  return { open, setOpen };
}

// エリアを表現するコンポーネント。
// 折り畳みロジックやエリアヘッダーの描画・拠点の描画を担う。
export function AreaSection({
  // エリア表示制御
  area,
  accommodationCount,
  hidden,
  dimmed,
  // フィルタリングに使用 + SiteSection に透過
  selected,
  // カレンダー描画 → SiteSection にまとめて渡す
  ...siteSectionProps
}: AreaSectionProps) {
  const { hasAccommodationFilter, areaFilter } =
    useAccommodationFilterContext();

  const { open, setOpen } = useAreaSectionOpen(area.slug, areaFilter);

  // hasFilteredRooms: フィルタ適用後にこのエリアに表示すべき部屋があるか
  // selectedの物件が属するエリアはフィルタに関係なく表示する
  const hasSelectedAccommodation =
    selected !== null &&
    area.sites.some((site) =>
      site.accommodations.some((a) => a.id === selected.accommodationId),
    );
  const hasFilteredRooms =
    hasSelectedAccommodation ||
    !hasAccommodationFilter ||
    (accommodationCount?.available ?? 0) > 0;

  const isHidden = hidden || !hasFilteredRooms;

  const sortedSites = useMemo(() => sortSites(area.sites), [area.sites]);

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} hidden={isHidden}>
      <AreaHeader
        areaData={area}
        accommodationCount={accommodationCount ?? null}
        dimmed={dimmed}
      />
      <Collapsible.Panel
        className="h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-200 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0"
        keepMounted={true}
      >
        <div>
          {sortedSites.map((site) => (
            <SiteSection
              key={site.id}
              site={site}
              selected={selected}
              {...siteSectionProps}
            />
          ))}
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
